import { Bcp47Locale, LocaleOption } from "../types";

export const DEFAULT_LOCALE = "en-US" as Bcp47Locale;

type LocaleInput = Omit<LocaleOption, "value"> & { value: string };

const locale = ({ value, label, language, region }: LocaleInput): LocaleOption => ({
  value: value as Bcp47Locale,
  label,
  language,
  region
});

export const LOCALE_OPTIONS: LocaleOption[] = [
  locale({ value: "en-US", label: "English (United States)", language: "English", region: "United States" }),
  locale({ value: "en-GB", label: "English (United Kingdom)", language: "English", region: "United Kingdom" }),
  locale({ value: "en-AU", label: "English (Australia)", language: "English", region: "Australia" }),
  locale({ value: "en-NZ", label: "English (New Zealand)", language: "English", region: "New Zealand" }),
  locale({ value: "en-CA", label: "English (Canada)", language: "English", region: "Canada" }),
  locale({ value: "en-IE", label: "English (Ireland)", language: "English", region: "Ireland" }),
  locale({ value: "en-ZA", label: "English (South Africa)", language: "English", region: "South Africa" }),
  locale({ value: "en-IN", label: "English (India)", language: "English", region: "India" }),
  locale({ value: "en-SG", label: "English (Singapore)", language: "English", region: "Singapore" }),
  locale({ value: "en-PH", label: "English (Philippines)", language: "English", region: "Philippines" })
];

export const SUPPORTED_LANGUAGES = [
  "English"
] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// Pattern for validating against the supported locale formats in LOCALE_OPTIONS only.
// This validates the 'xx' or 'xx-XX' format used by our supported locales.
// Note: Full BCP 47 compliance would require handling script subtags (e.g., 'zh-Hans-CN'),
// extended subtags, and other variants. For comprehensive BCP 47 validation,
// use isValidBcp47Locale() which checks against the supported options list.
export const BCP47_LOCALE_PATTERN = /^[a-z]{2}(-[A-Z]{2})?$/;

export const isValidBcp47Locale = (value: string): value is Bcp47Locale =>
  LOCALE_OPTIONS.some((option) => option.value === value);

export const getLocaleByValue = (value: string): LocaleOption | undefined =>
  LOCALE_OPTIONS.find((option) => option.value === value);

export const getLocalesByLanguage = (language: SupportedLanguage): LocaleOption[] =>
  LOCALE_OPTIONS.filter((option) => option.language === language);

export const formatLocaleLabel = (option: LocaleOption): string =>
  `${option.language} (${option.region})`;
